import { useState, useCallback } from 'react';
import { format, addDays, startOfWeek, endOfWeek } from 'date-fns';
import availabilityAPI from '../../../api/availability';
import { formatUTCToISTTime, extractISTTimeComponents } from '../../../utils/timezone';

/**
 * Custom hook for managing doctor availability and slots
 * Handles CRUD operations for availability blocks with API integration
 */
export const useAvailability = (doctorId) => {
  const [availabilityBlocks, setAvailabilityBlocks] = useState([]);
  const [weeklyView, setWeeklyView] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [previewSlots, setPreviewSlots] = useState([]);

  /**
   * Load availability blocks for a specific date
   * @param {string} date - Date in YYYY-MM-DD format
   */
  const loadDayAvailability = useCallback(async (date) => {
    if (!doctorId || !date) {
      setAvailabilityBlocks([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await availabilityAPI.getDoctorAvailabilityBlocks(doctorId, { date });
      
      if (result.success && result.data?.blocks) {
        // Transform API data to include calculated slot counts
        const transformedBlocks = result.data.blocks.map(block => ({
          ...block,
          totalSlots: block.slots?.length || 0,
          availableSlots: block.slots?.filter(slot => slot.status === 'Open').length || 0,
          bookedSlots: block.slots?.filter(slot => slot.status === 'Booked').length || 0,
          id: block._id // Add id field for consistency
        }));
        console.log('Transformed availability blocks:', transformedBlocks);
        setAvailabilityBlocks(transformedBlocks);
      } else {
        // Fallback to mock data for development
        setAvailabilityBlocks(generateMockBlocks(date));
      }
    } catch (error) {
      console.error('Error loading day availability:', error);
      setError('Failed to load availability');
      // Use mock data on error
      setAvailabilityBlocks(generateMockBlocks(date));
    } finally {
      setLoading(false);
    }
  }, [doctorId]);

  /**
   * Load weekly availability overview
   * @param {Date} selectedDate - Selected date to get week view for
   */
  const loadWeekAvailability = useCallback(async (selectedDateOrRange) => {
    if (!doctorId || !selectedDateOrRange) {
      setWeeklyView([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Determine range
      let rangeStart, rangeEnd;
      if (selectedDateOrRange && typeof selectedDateOrRange === 'object' && selectedDateOrRange.startDate) {
        rangeStart = new Date(selectedDateOrRange.startDate);
        rangeEnd = new Date(selectedDateOrRange.endDate || selectedDateOrRange.startDate);
      } else {
        rangeStart = selectedDateOrRange instanceof Date ? selectedDateOrRange : new Date(selectedDateOrRange);
        rangeEnd = addDays(rangeStart, 6); // default 7-day window
      }

      // Cap the range to maximum 7 days to keep UI consistent
      const maxEnd = addDays(rangeStart, 6);
      if (rangeEnd > maxEnd) rangeEnd = maxEnd;

      const startStr = format(rangeStart, 'yyyy-MM-dd');
      const endStr = format(rangeEnd, 'yyyy-MM-dd');

      // Try optimized single request for range
      let result;
      try {
        result = await availabilityAPI.getDoctorAvailabilityBlocks(doctorId, { startDate: startStr, endDate: endStr });
      } catch (e) {
        result = null;
      }

      const days = [];
      for (let d = new Date(rangeStart); d <= rangeEnd; d = addDays(d, 1)) {
        days.push(new Date(d));
      }

      if (result && (result.success || Array.isArray(result?.data?.blocks) || Array.isArray(result?.blocks))) {
        // Handle possible shapes: { success, data: { blocks } } or { blocks } or array
        const blocks = Array.isArray(result?.data?.blocks)
          ? result.data.blocks
          : Array.isArray(result?.blocks)
            ? result.blocks
            : Array.isArray(result)
              ? result
              : [];

        // Group blocks by date string
        const byDate = blocks.reduce((acc, block) => {
          const dateKey = block.date || block.dateStr || (block.startTime ? new Date(block.startTime).toISOString().slice(0,10) : null);
          if (!dateKey) return acc;
          if (!acc[dateKey]) acc[dateKey] = [];
          acc[dateKey].push(block);
          return acc;
        }, {});

        const weekData = days.map((date) => {
          const dateStr = format(date, 'yyyy-MM-dd');
          const dayName = format(date, 'EEE');
          const dayBlocks = (byDate[dateStr] || []).map((block) => ({
            ...block,
            id: block._id || block.id,
            totalSlots: block.slots?.length || 0,
            availableSlots: block.slots?.filter((s) => s.status === 'Open').length || 0,
            bookedSlots: block.slots?.filter((s) => s.status === 'Booked').length || 0,
          }));

          const flatSlots = dayBlocks.flatMap((block) =>
            (block.slots || []).map((slot) => ({
              slotId: slot._id || slot.slotId,
              blockId: block._id || block.id,
              startTime: formatUTCToISTTime(slot.startTime),
              endTime: formatUTCToISTTime(slot.endTime),
              status: slot.status || (slot.bookedAppointments?.length ? 'Booked' : 'Open'),
              originalStartTime: slot.startTime,
              originalEndTime: slot.endTime,
            }))
          );

          const summary = {
            totalSlots: dayBlocks.reduce((acc, b) => acc + (b.totalSlots || 0), 0),
            bookedSlots: dayBlocks.reduce((acc, b) => acc + (b.bookedSlots || 0), 0),
            availableSlots: dayBlocks.reduce((acc, b) => acc + (b.availableSlots || 0), 0),
          };

          return { date, dateStr, dayName, blocks: dayBlocks, slots: flatSlots, summary };
        });

        setWeeklyView(weekData);
      } else {
        // Fallback: perform minimal per-day requests (only one per day, max 7)
        const dayPromises = days.map((date, i) => {
          const dateStr = format(date, 'yyyy-MM-dd');
          return availabilityAPI
            .getDoctorAvailabilityBlocks(doctorId, { date: dateStr })
            .then((res) => {
              const dayName = format(date, 'EEE');
              if (res?.success && Array.isArray(res.data?.blocks)) {
                const transformedBlocks = res.data.blocks.map((block) => ({
                  ...block,
                  id: block._id || block.id,
                  totalSlots: block.slots?.length || 0,
                  availableSlots: block.slots?.filter((s) => s.status === 'Open').length || 0,
                  bookedSlots: block.slots?.filter((s) => s.status === 'Booked').length || 0,
                }));
                const flatSlots = (res.data.blocks || []).flatMap((block) =>
                  (block.slots || []).map((slot) => ({
                    slotId: slot._id || slot.slotId,
                    blockId: block._id || block.id,
                    startTime: formatUTCToISTTime(slot.startTime),
                    endTime: formatUTCToISTTime(slot.endTime),
                    status: slot.status || (slot.bookedAppointments?.length ? 'Booked' : 'Open'),
                    originalStartTime: slot.startTime,
                    originalEndTime: slot.endTime,
                  }))
                );
                const summary = {
                  totalSlots: transformedBlocks.reduce((acc, b) => acc + (b.totalSlots || 0), 0),
                  bookedSlots: transformedBlocks.reduce((acc, b) => acc + (b.bookedSlots || 0), 0),
                  availableSlots: transformedBlocks.reduce((acc, b) => acc + (b.availableSlots || 0), 0),
                };
                return { date, dateStr, dayName, blocks: transformedBlocks, slots: flatSlots, summary, success: true };
              }
              return { date, dateStr, dayName, blocks: generateMockWeeklyBlocks(i), slots: [], summary: { totalSlots: 0, bookedSlots: 0, availableSlots: 0 }, success: false };
            })
            .catch(() => ({ date, dateStr, dayName: format(date, 'EEE'), blocks: generateMockWeeklyBlocks(i), slots: [], summary: { totalSlots: 0, bookedSlots: 0, availableSlots: 0 }, success: false }));
        });
        const weekData = await Promise.all(dayPromises);
        setWeeklyView(weekData);
      }
    } catch (err) {
      console.error('Error loading week availability:', err);
      setError('Failed to load weekly availability');
      
      // Generate mock weekly data based on sliding window
      const mockData = [];
      const start = selectedDateOrRange instanceof Date ? selectedDateOrRange : new Date(selectedDateOrRange?.startDate || new Date());
      for (let i = 0; i < 7; i++) {
        const date = addDays(start, i);
        mockData.push({
          date,
          dateStr: format(date, 'yyyy-MM-dd'),
          dayName: format(date, 'EEE'),
          blocks: generateMockWeeklyBlocks(i),
          slots: [],
          summary: { totalSlots: 0, bookedSlots: 0, availableSlots: 0 },
        });
      }
      setWeeklyView(mockData);
    } finally {
      setLoading(false);
    }
  }, [doctorId]);

  /**
   * Create new availability block
   * @param {Object} blockData - Availability block data
   */
  const createAvailabilityBlock = useCallback(async (blockData) => {
    console.log('createAvailabilityBlock called with:', { doctorId, blockData });
    
    if (!doctorId) {
      throw new Error('Doctor ID is required');
    }
    
    if (!blockData) {
      throw new Error('Block data is required');
    }

    setSaving(true);
    setError(null);

    try {
      // Ensure proper data format for the new backend API
      const formattedData = {
        date: blockData.date,
        startTime: blockData.startTime,
        endTime: blockData.endTime,
        slotMinutes: Number(blockData.slotMinutes) || 30,
        bufferMinutes: Number(blockData.bufferMinutes) || 0
      };

      console.log('Formatted availability block data:', formattedData);

      const result = await availabilityAPI.createAvailabilityBlock(doctorId, formattedData);
      
      if (result.success) {
        // Reload availability for the created date
        await loadDayAvailability(blockData.date);
        // Clear preview slots after successful creation
        setPreviewSlots([]);
        return true;
      } else {
        throw new Error(result.message || 'Failed to create availability block');
      }
    } catch (error) {
      console.error('Error creating availability block:', error);
      setError(error.message || 'Failed to create availability block');
      throw error;
    } finally {
      setSaving(false);
    }
  }, [doctorId, loadDayAvailability]);

  /**
   * Get available slots for a specific date
   * @param {string} date - Date in YYYY-MM-DD format
   */
  const getAvailableSlots = useCallback(async (date) => {
    if (!doctorId || !date) return [];

    try {
      const result = await availabilityAPI.getDoctorSlots(doctorId, date);
      
      if (result.success && result.data?.slots) {
        return result.data.slots;
      } else {
        return [];
      }
    } catch (error) {
      console.error('Error getting available slots:', error);
      return [];
    }
  }, [doctorId]);

  /**
   * Update preview slots based on form data
   * @param {Object} formData - Form data to generate preview from
   */
  const updatePreview = useCallback((formData) => {
    try {
      if (!formData.startTime || !formData.endTime || !formData.slotMinutes) {
        setPreviewSlots([]);
        return;
      }

      // Match the new backend API structure for preview
      const previewData = {
        date: formData.date,
        startTime: formData.startTime,
        endTime: formData.endTime,
        slotMinutes: parseInt(formData.slotMinutes) || 30,
        bufferMinutes: parseInt(formData.bufferMinutes) || 0
      };

      console.log('Generating preview with buffer time:', previewData);

      const preview = availabilityAPI.previewSlots(previewData);
      
      if (preview.success && preview.slots) {
        const formattedSlots = preview.slots.map((slot, index) => ({
          id: index + 1,
          startTime: slot.startTime,
          endTime: slot.endTime,
          duration: slot.duration,
          bufferTime: previewData.bufferMinutes
        }));
        setPreviewSlots(formattedSlots);
        console.log('Preview slots with buffer time:', formattedSlots);
      } else {
        setPreviewSlots([]);
        if (preview.message) {
          console.warn('Preview error:', preview.message);
        }
      }
    } catch (error) {
      console.error('Error updating preview:', error);
      setPreviewSlots([]);
    }
  }, []);

  /**
   * Update an availability block
   * @param {string} blockId - Availability block ID
   * @param {Object} blockData - Updated availability block data
   */
  const updateAvailabilityBlock = useCallback(async (blockId, blockData) => {
    if (!blockId || !blockData) {
      throw new Error('Block ID and block data are required');
    }

    setSaving(true);
    setError(null);

    try {
      // Ensure proper data format for the backend API
      const formattedData = {
        date: blockData.date,
        startTime: blockData.startTime,
        endTime: blockData.endTime,
        slotMinutes: Number(blockData.slotMinutes) || 30,
        bufferMinutes: Number(blockData.bufferMinutes) || 0
      };

      console.log('Updating availability block with data:', formattedData);

      const result = await availabilityAPI.updateAvailabilityBlock(blockId, formattedData);
      
      if (result.success) {
        // Reload availability for the updated date
        await loadDayAvailability(blockData.date);
        return true;
      } else {
        throw new Error(result.message || 'Failed to update availability block');
      }
    } catch (error) {
      console.error('Error updating availability block:', error);
      setError(error.message || 'Failed to update availability block');
      throw error;
    } finally {
      setSaving(false);
    }
  }, [loadDayAvailability]);

  /**
   * Delete an availability block
   * @param {string} blockId - Availability block ID
   * @param {string} date - Date to reload after deletion
   */
  const deleteAvailabilityBlock = useCallback(async (blockId, date) => {
    if (!blockId) {
      throw new Error('Block ID is required');
    }

    setSaving(true);
    setError(null);

    try {
      const result = await availabilityAPI.deleteAvailabilityBlock(blockId);
      
      if (result.success) {
        // Reload availability for the current date
        await loadDayAvailability(date);
        return true;
      } else {
        throw new Error(result.message || 'Failed to delete availability block');
      }
    } catch (error) {
      console.error('Error deleting availability block:', error);
      setError(error.message || 'Failed to delete availability block');
      throw error;
    } finally {
      setSaving(false);
    }
  }, [loadDayAvailability]);

  /**
   * Reset all availability state
   */
  const resetAvailability = useCallback(() => {
    setAvailabilityBlocks([]);
    setWeeklyView([]);
    setPreviewSlots([]);
    setError(null);
  }, []);

  // Utility functions for mock data (for development/fallback)
  const generateMockBlocks = useCallback((date) => [
    {
      id: '1',
      date: date,
      startTime: '09:00',
      endTime: '12:00',
      slotMinutes: 30,
      maxParallel: 1,
      totalSlots: 6,
      availableSlots: 4,
      bookedSlots: 2,
      status: 'active'
    },
    {
      id: '2',
      date: date,
      startTime: '14:00',
      endTime: '18:00',
      slotMinutes: 30,
      maxParallel: 1,
      totalSlots: 8,
      availableSlots: 6,
      bookedSlots: 2,
      status: 'active'
    }
  ], []);

  const generateMockWeeklyBlocks = useCallback((dayIndex) => {
    if (dayIndex === 0 || dayIndex === 6) return []; // Sunday and Saturday
    return [
      {
        startTime: '09:00',
        endTime: '12:00',
        availableSlots: Math.floor(Math.random() * 5) + 1
      },
      {
        startTime: '14:00',
        endTime: '18:00',
        availableSlots: Math.floor(Math.random() * 8) + 2
      }
    ];
  }, []);

  /**
   * Transform slots for weekly view (individual slots)
   */
  const transformSlotsForWeeklyView = useCallback((slots) => {
    console.log("Raw slots for weekly view:", slots);
    
    return slots.map((slot) => ({
      slotId: slot.slotId,
      blockId: slot.blockId,
      startTime: formatUTCToISTTime(slot.startTime),
      endTime: formatUTCToISTTime(slot.endTime),
      availableCapacity: slot.availableCapacity || 0,
      originalStartTime: slot.startTime,
      originalEndTime: slot.endTime
    }));
  }, []);

  const transformSlotsToBlocks = useCallback((slots) => {
    // Group slots by block ID to create time blocks
    const blocks = {};
    console.log("Raw slots from API:", slots);

    slots.forEach((slot) => {
      const blockId = slot.blockId;
      
      // Convert UTC times to IST
      const startTimeIST = formatUTCToISTTime(slot.startTime);
      const endTimeIST = formatUTCToISTTime(slot.endTime);
      
      if (!blocks[blockId]) {
        blocks[blockId] = {
          blockId: blockId,
          slotId: slot.slotId,
          startTime: startTimeIST,
          endTime: endTimeIST,
          availableSlots: 0,
          totalSlots: 0,
          slots: []
        };
      }
      
      // Update end time if this slot extends the block
      if (endTimeIST > blocks[blockId].endTime) {
        blocks[blockId].endTime = endTimeIST;
      }
      
      // Count available slots based on capacity
      blocks[blockId].totalSlots++;
      if (slot.availableCapacity && slot.availableCapacity > 0) {
        blocks[blockId].availableSlots++;
      }
      
      // Store slot details
      blocks[blockId].slots.push({
        slotId: slot.slotId,
        startTime: startTimeIST,
        endTime: endTimeIST,
        availableCapacity: slot.availableCapacity || 0,
        originalStartTime: slot.startTime,
        originalEndTime: slot.endTime
      });
    });
    
    console.log("Transformed blocks with IST times:", blocks);
    return Object.values(blocks);
  }, []);

  return {
    // State
    availabilityBlocks,
    weeklyView,
    loading,
    saving,
    error,
    previewSlots,

    // Actions
    loadDayAvailability,
    loadWeekAvailability,
    createAvailabilityBlock,
    updateAvailabilityBlock,
    deleteAvailabilityBlock,
    getAvailableSlots,
    updatePreview,
    resetAvailability
  };
};

export default useAvailability;
